import Phaser from 'phaser';
import { PALETTE } from '../theme';
import { noteDrag } from './tap';

/**
 * Ein Bereich, dessen Inhalt laenger sein darf als der Bildschirm.
 *
 * Die Beweisakte hoerte frueher bei `height - 140` einfach auf zu zeichnen —
 * mit vierzehn Hinweisen fielen ausgerechnet die harten aus Akt III, die den
 * Fall entscheiden, unten heraus. Auf einem Telefon traf das jede laengere
 * Seite.
 *
 * Bewusst ohne Maske: Masken sind in Phaser 4 ein Renderfilter, der je nach
 * Renderer anders arbeitet. Stattdessen verschwindet, was nicht vollstaendig
 * im Sichtfenster liegt. Die Zeilen sind kurz genug, dass das nicht stoert,
 * und es funktioniert auf WebGL und Canvas gleich.
 *
 * Bedient wird mit Mausrad, Ziehen (Finger oder Maus) und dem Balken rechts.
 */

export interface ScrollArea {
  /** In diesen Container gehoert der Inhalt; y = 0 ist die Oberkante. */
  readonly content: Phaser.GameObjects.Container;
  /** Nach dem Befuellen aufrufen: misst den Inhalt und richtet alles ein. */
  readonly finish: () => void;
  readonly scrollTo: (offset: number) => void;
  /** Wie weit gerade gescrollt ist — zum Wiederherstellen nach einem Neuaufbau. */
  readonly offset: () => number;
  readonly destroy: () => void;
}

export function createScrollArea(
  scene: Phaser.Scene,
  rect: { x: number; y: number; width: number; height: number },
): ScrollArea {
  const content = scene.add.container(rect.x, rect.y);
  const bar = scene.add.rectangle(rect.x + rect.width + 6, rect.y, 3, 10, PALETTE.coast, 0.5);
  bar.setOrigin(0, 0).setVisible(false);

  let offset = 0;
  let contentHeight = 0;
  let destroyed = false;

  const maxOffset = (): number => Math.max(0, contentHeight - rect.height);

  const inside = (pointer: Phaser.Input.Pointer): boolean =>
    pointer.x >= rect.x &&
    pointer.x <= rect.x + rect.width + 16 &&
    pointer.y >= rect.y &&
    pointer.y <= rect.y + rect.height;

  const apply = (): void => {
    offset = Math.max(0, Math.min(maxOffset(), offset));
    content.y = rect.y - offset;

    // Nur zeigen, was ganz hineinpasst — siehe oben, warum ohne Maske.
    for (const child of content.list as unknown as Phaser.GameObjects.Components.Visible[]) {
      const object = child as unknown as { y: number; height?: number; displayHeight?: number };
      const top = object.y - offset;
      const height = object.displayHeight ?? object.height ?? 0;
      child.setVisible(top >= -1 && top + height <= rect.height + 1);
    }

    const overflow = maxOffset();
    bar.setVisible(overflow > 0);
    if (overflow > 0) {
      const barHeight = Math.max(24, rect.height * (rect.height / contentHeight));
      bar.height = barHeight;
      bar.y = rect.y + (rect.height - barHeight) * (offset / overflow);
    }
  };

  const onWheel = (pointer: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number): void => {
    if (!inside(pointer)) return;
    offset += dy * 0.6;
    apply();
  };

  let dragStart: { y: number; offset: number } | null = null;
  const onDown = (pointer: Phaser.Input.Pointer): void => {
    if (inside(pointer)) dragStart = { y: pointer.y, offset };
  };
  const onMove = (pointer: Phaser.Input.Pointer): void => {
    if (dragStart === null || !pointer.isDown) return;
    const dy = pointer.y - dragStart.y;
    // Erst ab einigen Pixeln ist es ein Ziehen — und dann kein Tippen mehr.
    if (Math.abs(dy) > 8) noteDrag();
    offset = dragStart.offset - dy;
    apply();
  };
  const onUp = (): void => {
    dragStart = null;
  };

  scene.input.on('wheel', onWheel);
  scene.input.on('pointerdown', onDown);
  scene.input.on('pointermove', onMove);
  scene.input.on('pointerup', onUp);
  scene.input.on('pointerupoutside', onUp);

  const destroy = (): void => {
    if (destroyed) return;
    destroyed = true;
    scene.input.off('wheel', onWheel);
    scene.input.off('pointerdown', onDown);
    scene.input.off('pointermove', onMove);
    scene.input.off('pointerup', onUp);
    scene.input.off('pointerupoutside', onUp);
    if (content.active) content.destroy();
    if (bar.active) bar.destroy();
  };
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, destroy);

  return {
    content,
    finish: () => {
      contentHeight = 0;
      for (const child of content.list) {
        const object = child as unknown as { y: number; height?: number; displayHeight?: number };
        contentHeight = Math.max(
          contentHeight,
          object.y + (object.displayHeight ?? object.height ?? 0),
        );
      }
      apply();
    },
    offset: () => offset,
    scrollTo: (value: number) => {
      offset = value;
      apply();
    },
    destroy,
  };
}
